/**
 * useAnalytics Hook
 * 
 * Privacy-safe analytics hook for tracking user events.
 * - Automatically respects user consent
 * - Sanitizes event properties
 * - Provides type-safe event tracking
 * 
 * @example
 * const { trackEvent, trackPageView } = useAnalytics();
 * trackEvent('course_enrolled', { courseId: '123' });
 */

import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { useAuthStore } from '../store/auth';
import {
  hasAnalyticsConsent,
  identifyUser,
  resetIdentity,
  trackEvent as baseTrackEvent,
  trackPageView as baseTrackPageView,
} from '../lib/posthog';

/**
 * Predefined event names for consistency across the application.
 * 
 * Naming Convention:
 * - Use snake_case for event names
 * - Start with the object (course, user, meeting, etc.)
 * - End with the action (viewed, enrolled, completed, etc.)
 */
export const AnalyticsEvents = {
  // ============================================
  // AUTHENTICATION EVENTS
  // ============================================
  /** User successfully created a new account */
  USER_SIGNED_UP: 'user_signed_up',
  /** User logged in successfully */
  USER_SIGNED_IN: 'user_signed_in',
  /** User logged out */
  USER_SIGNED_OUT: 'user_signed_out',
  /** User verified their email */
  USER_EMAIL_VERIFIED: 'user_email_verified',
  /** User requested password reset */
  USER_PASSWORD_RESET_REQUESTED: 'user_password_reset_requested',
  /** User updated their profile */
  USER_PROFILE_UPDATED: 'user_profile_updated',

  // ============================================
  // COURSE DISCOVERY EVENTS
  // ============================================
  /** User viewed the courses listing page */
  COURSES_PAGE_VIEWED: 'courses_page_viewed',
  /** User searched for courses */
  COURSES_SEARCHED: 'courses_searched',
  /** User applied a category filter */
  COURSES_FILTERED_BY_CATEGORY: 'courses_filtered_by_category',
  /** User applied a level filter */
  COURSES_FILTERED_BY_LEVEL: 'courses_filtered_by_level',
  /** User viewed a specific course detail page */
  COURSE_VIEWED: 'course_viewed',
  /** User expanded a course section to view lessons */
  COURSE_SECTION_EXPANDED: 'course_section_expanded',
  /** User clicked on instructor profile */
  INSTRUCTOR_PROFILE_CLICKED: 'instructor_profile_clicked',

  // ============================================
  // ENROLLMENT & PAYMENT EVENTS
  // ============================================
  /** User enrolled in a free course */
  COURSE_ENROLLED_FREE: 'course_enrolled_free',
  /** User started checkout for paid course */
  CHECKOUT_STARTED: 'checkout_started',
  /** Payment completed successfully (track after redirect) */
  PAYMENT_COMPLETED: 'payment_completed',
  /** User cancelled their enrollment */
  ENROLLMENT_CANCELLED: 'enrollment_cancelled',

  // ============================================
  // LEARNING & PROGRESS EVENTS
  // ============================================
  /** User started watching/reading a lesson */
  LESSON_STARTED: 'lesson_started',
  /** User completed a lesson */
  LESSON_COMPLETED: 'lesson_completed',
  /** User navigated to next lesson */
  LESSON_NAVIGATED_NEXT: 'lesson_navigated_next',
  /** User navigated to previous lesson */
  LESSON_NAVIGATED_PREVIOUS: 'lesson_navigated_previous',
  /** User completed an entire course */
  COURSE_COMPLETED: 'course_completed',
  /** User reached a progress milestone (25%, 50%, 75%) */
  COURSE_PROGRESS_MILESTONE: 'course_progress_milestone',
  /** User downloaded a course document */
  DOCUMENT_DOWNLOADED: 'document_downloaded',

  // ============================================
  // LIVE MEETING EVENTS
  // ============================================
  /** User joined a live meeting */
  MEETING_JOINED: 'meeting_joined',
  /** Professor started a meeting */
  MEETING_STARTED: 'meeting_started',
  /** Meeting ended */
  MEETING_ENDED: 'meeting_ended',
  /** User viewed the meeting recordings */
  MEETING_RECORDING_VIEWED: 'meeting_recording_viewed',

  // ============================================
  // ENGAGEMENT EVENTS
  // ============================================
  /** User submitted a course review */
  REVIEW_SUBMITTED: 'review_submitted',
  /** User viewed reviews section */
  REVIEWS_VIEWED: 'reviews_viewed',
  /** User created a support ticket */
  SUPPORT_TICKET_CREATED: 'support_ticket_created',
  /** User closed a support ticket */
  SUPPORT_TICKET_CLOSED: 'support_ticket_closed',

  // ============================================
  // PROFESSOR/INSTRUCTOR EVENTS
  // ============================================
  /** User applied to become an instructor */
  INSTRUCTOR_APPLICATION_STARTED: 'instructor_application_started',
  /** Professor created a new course */
  COURSE_CREATED: 'course_created',
  /** Professor published a course */
  COURSE_PUBLISHED: 'course_published',
  /** Professor scheduled a meeting */
  MEETING_SCHEDULED: 'meeting_scheduled',
  /** Professor viewed analytics dashboard */
  ANALYTICS_VIEWED: 'analytics_viewed',

  // ============================================
  // NAVIGATION & UI EVENTS
  // ============================================
  /** User clicked on CTA button */
  CTA_CLICKED: 'cta_clicked',
  /** User opened mobile sidebar */
  MOBILE_SIDEBAR_OPENED: 'mobile_sidebar_opened',
  /** User used the help center */
  HELP_CENTER_VIEWED: 'help_center_viewed',
  /** Error displayed to user */
  ERROR_DISPLAYED: 'error_displayed',
} as const;

export type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

/**
 * Properties that should NEVER be tracked (sanitized out)
 */
const SENSITIVE_PROPERTY_KEYS = [
  'password',
  'token',
  'jwt',
  'secret',
  'creditCard',
  'cardNumber',
  'cvv',
  'ssn',
  'email', // We track user by ID, not email
  'phone',
  'address',
  'ip',
];

/**
 * Sanitize properties by removing sensitive data
 */
function sanitizeProperties(
  properties?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!properties) return undefined;

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(properties)) {
    const lowerKey = key.toLowerCase();
    
    // Skip sensitive keys
    if (SENSITIVE_PROPERTY_KEYS.some(sensitive => lowerKey.includes(sensitive))) {
      continue;
    }

    // Recursively sanitize nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeProperties(value as Record<string, unknown>);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Custom hook for analytics tracking
 */
export function useAnalytics() {
  const posthog = usePostHog();
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();

  // Identify user when they log in
  useEffect(() => {
    if (isAuthenticated && user && hasAnalyticsConsent()) {
      identifyUser(user.firebaseUid, {
        role: user.role,
        createdAt: user.createdAt,
      });
    }
  }, [isAuthenticated, user]);

  // Reset identity on logout
  useEffect(() => {
    if (!isAuthenticated) {
      resetIdentity();
    }
  }, [isAuthenticated]);

  // Track page views on route change
  useEffect(() => {
    if (hasAnalyticsConsent()) {
      // PostHog auto-captures pageviews, but we can enhance with route info
      baseTrackPageView(location.pathname);
    }
  }, [location.pathname]);

  /**
   * Track a custom event with sanitized properties
   */
  const trackEvent = useCallback(
    (eventName: AnalyticsEventName | string, properties?: Record<string, unknown>) => {
      const sanitizedProps = sanitizeProperties(properties);
      baseTrackEvent(eventName, sanitizedProps);
    },
    []
  );

  /**
   * Track page view manually
   */
  const trackPageView = useCallback((pageName?: string) => {
    baseTrackPageView(pageName);
  }, []);

  /**
   * Check if analytics is enabled
   */
  const isAnalyticsEnabled = useCallback(() => {
    return hasAnalyticsConsent();
  }, []);

  /**
   * Get the PostHog distinct ID (for debugging)
   */
  const getDistinctId = useCallback(() => {
    return posthog?.get_distinct_id?.() ?? null;
  }, [posthog]);

  // ============================================
  // CONVENIENCE TRACKING METHODS
  // ============================================

  /**
   * Track course view
   */
  const trackCourseViewed = useCallback(
    (courseId: string, courseTitle: string, courseType: 'RECORDED' | 'LIVE', price: number) => {
      trackEvent(AnalyticsEvents.COURSE_VIEWED, {
        courseId,
        courseTitle,
        courseType,
        isFree: price === 0,
        priceRange: price === 0 ? 'free' : price < 50 ? 'low' : price < 100 ? 'medium' : 'high',
      });
    },
    [trackEvent]
  );

  /**
   * Track enrollment
   */
  const trackEnrollment = useCallback(
    (courseId: string, courseTitle: string, isFree: boolean) => {
      trackEvent(isFree ? AnalyticsEvents.COURSE_ENROLLED_FREE : AnalyticsEvents.CHECKOUT_STARTED, {
        courseId,
        courseTitle,
      });
    },
    [trackEvent]
  );

  /**
   * Track lesson progress
   */
  const trackLessonProgress = useCallback(
    (
      action: 'started' | 'completed' | 'next' | 'previous',
      lessonId: string,
      lessonTitle: string,
      lessonType: string,
      courseId: string
    ) => {
      const eventMap = {
        started: AnalyticsEvents.LESSON_STARTED,
        completed: AnalyticsEvents.LESSON_COMPLETED,
        next: AnalyticsEvents.LESSON_NAVIGATED_NEXT,
        previous: AnalyticsEvents.LESSON_NAVIGATED_PREVIOUS,
      };

      trackEvent(eventMap[action], {
        lessonId,
        lessonTitle,
        lessonType,
        courseId,
      });
    },
    [trackEvent]
  );

  /**
   * Track search
   */
  const trackSearch = useCallback(
    (query: string, resultsCount: number, filters?: Record<string, string>) => {
      trackEvent(AnalyticsEvents.COURSES_SEARCHED, {
        // Don't track the actual query content, just metadata
        queryLength: query.length,
        hasResults: resultsCount > 0,
        resultsCount,
        ...filters,
      });
    },
    [trackEvent]
  );

  /**
   * Track meeting join
   */
  const trackMeetingJoined = useCallback(
    (meetingId: string, courseId: string, isHost: boolean) => {
      trackEvent(AnalyticsEvents.MEETING_JOINED, {
        meetingId,
        courseId,
        isHost,
      });
    },
    [trackEvent]
  );

  /**
   * Track errors (for debugging, without sensitive info)
   */
  const trackError = useCallback(
    (errorType: string, errorContext: string) => {
      trackEvent(AnalyticsEvents.ERROR_DISPLAYED, {
        errorType,
        errorContext,
        path: location.pathname,
      });
    },
    [trackEvent, location.pathname]
  );

  return {
    trackEvent,
    trackPageView,
    isAnalyticsEnabled,
    getDistinctId,
    // Convenience methods
    trackCourseViewed,
    trackEnrollment,
    trackLessonProgress,
    trackSearch,
    trackMeetingJoined,
    trackError,
    // Export event names for convenience
    events: AnalyticsEvents,
  };
}

/**
 * Hook for tracking component visibility (impressions)
 */
export function useTrackImpression(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: { threshold?: number; triggerOnce?: boolean }
) {
  const { trackEvent } = useAnalytics();
  const { triggerOnce = true } = options ?? {};

  useEffect(() => {
    // Implementation would use IntersectionObserver
    // For now, we'll just track on mount if consent is given
    if (hasAnalyticsConsent() && triggerOnce) {
      trackEvent(eventName, properties);
    }
  }, [eventName, properties, triggerOnce, trackEvent]);
}

export default useAnalytics;
